interface BasisTheoryInitializationConfig {
  enabled: boolean;
  publicKey: string;
}

interface BasisTheoryState {
  whitelabelConfig?: {
    env?: {
      shared?: {
        basisTheory?: {
          enabled?: unknown;
          engines?: unknown;
          publicKey?: unknown;
        } | null;
      } | null;
    };
  };
}

type BtCapabilityStatus = 'available' | 'config_incomplete' | 'disabled' | 'method_unavailable';

interface BtCapabilities {
  binValidationEnabled: boolean;
  checkoutEnabled: boolean;
  installmentsEnabled: boolean;
  showInstallmentsInterest: boolean;
  status: BtCapabilityStatus;
}

interface PaymentMethodIdentity {
  engine?: unknown;
  type?: unknown;
}

interface BtFeatureFlags {
  BIN_VALIDATION_FOR_INSTALLMENTS_ENABLED?: unknown;
  INSTALLMENTS_ENABLED?: unknown;
  SHOW_INSTALLMENTS_INTEREST?: unknown;
}

interface BtCapabilitiesState extends BasisTheoryState {
  whitelabelConfig?: BasisTheoryState['whitelabelConfig'] & {
    features?: BtFeatureFlags;
  };
}

/** Normalize externally configured processor engines into comparable identifiers. */
function selectBasisTheoryEngines(state: BasisTheoryState): string[] {
  const engines = state.whitelabelConfig?.env?.shared?.basisTheory?.engines;
  if (!Array.isArray(engines)) return [];

  return Array.from(
    new Set(
      engines
        .filter((engine): engine is string => typeof engine === 'string')
        .map((engine) => engine.trim().toLowerCase())
        .filter(Boolean),
    ),
  );
}

/** Select the tenant configuration needed to initialize the Basis Theory SDK. */
function selectBasisTheoryInitializationConfig(
  state: BasisTheoryState,
): BasisTheoryInitializationConfig {
  const config = state.whitelabelConfig?.env?.shared?.basisTheory;
  const publicKey = typeof config?.publicKey === 'string' ? config.publicKey.trim() : '';
  const processorEngines = selectBasisTheoryEngines(state);

  return {
    enabled: config?.enabled === true && Boolean(publicKey) && processorEngines.length > 0,
    publicKey,
  };
}

/** Select the Basis Theory capabilities available for a payment method. */
function selectBtCapabilities(
  state: BtCapabilitiesState,
  paymentMethod: PaymentMethodIdentity | null | undefined,
): BtCapabilities {
  const config = state.whitelabelConfig?.env?.shared?.basisTheory;
  const features = state.whitelabelConfig?.features;
  const { publicKey } = selectBasisTheoryInitializationConfig(state);
  const processorEngines = selectBasisTheoryEngines(state);

  let status: BtCapabilityStatus = config?.enabled === true ? 'config_incomplete' : 'disabled';
  if (config?.enabled === true && publicKey && processorEngines.length) {
    const selectedEngine =
      typeof paymentMethod?.engine === 'string' ? paymentMethod.engine.trim().toLowerCase() : '';

    status =
      paymentMethod?.type === 'credit_card' && processorEngines.includes(selectedEngine)
        ? 'available'
        : 'method_unavailable';
  }

  return {
    binValidationEnabled: features?.BIN_VALIDATION_FOR_INSTALLMENTS_ENABLED === true,
    checkoutEnabled: status === 'available',
    installmentsEnabled: features?.INSTALLMENTS_ENABLED === true,
    showInstallmentsInterest: features?.SHOW_INSTALLMENTS_INTEREST === true,
    status,
  };
}

export type { BtCapabilities, BtCapabilitiesState, BtCapabilityStatus, PaymentMethodIdentity };
export { selectBasisTheoryInitializationConfig, selectBtCapabilities };
